import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { useCurator } from "../useCurator.js";

const STAGE_MS = 2600;

/**
 * После онбординга — короткая пауза «куратор читает ответы и выбирает».
 * Это не загрузчик: это жест — дать юзеру почувствовать, что подбор
 * делает человек, а не алгоритм. Параллельно фронт инвалидирует me.get,
 * чтобы подтянуть реальное письмо, если бекенд уже его сгенерил.
 */
const STAGES: { title: string; sub: string }[] = [
  {
    title: "читаю ваши ответы",
    sub: "ищу, за что зацепиться — тон, паузы, что вы обошли стороной",
  },
  {
    title: "перебираю карточки",
    sub: "у меня на столе сегодня одиннадцать — но вам подойдут две или три",
  },
  {
    title: "пишу письмо",
    sub: "оно придёт коротким — ровно столько, чтобы вы захотели познакомиться",
  },
];

export function Waiting(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const [stage, setStage] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Регистрация пройдена — при следующем старте бота App.tsx уведёт на /home,
    // минуя Splash/Consent/Basics/Photo/Onboarding.
    try {
      window.localStorage.setItem("companion:registered", "1");
    } catch {
      /* privacy mode */
    }
    const id = window.setInterval(() => {
      setStage((s) => {
        if (s + 1 >= STAGES.length) {
          window.clearInterval(id);
          window.setTimeout(() => {
            setReady(true);
          }, 500);
          return s;
        }
        return s + 1;
      });
    }, STAGE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  const current = STAGES[stage] ?? STAGES[0];

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="в редакции сейчас" />
      </div>

      <div className="flex flex-1 flex-col justify-center px-[22px]">
        <div className="editorial-kicker text-accent flex items-center gap-[8px]">
          <motion.span
            aria-hidden
            className="inline-block h-[7px] w-[7px] rounded-full bg-accent"
            animate={{ opacity: [1, 0.35, 1], scale: [1, 0.85, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
          <span>{curator.firstName} работает</span>
        </div>

        <motion.h1
          key={current?.title ?? "stage"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-[22px] font-display font-light leading-[1.05] tracking-[-0.022em] text-ink text-[40px]"
        >
          <em className="italic text-velvet">{current?.title}</em>
        </motion.h1>

        <motion.p
          key={current?.sub ?? "stage-sub"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-[14px] font-display italic font-light text-[16px] leading-[1.5] text-ink/70"
        >
          {current?.sub}
        </motion.p>

        <div className="mt-[36px]">
          <div className="flex items-center gap-[10px] editorial-meta text-ink/45">
            {STAGES.map((_, i) => (
              <span
                key={i}
                className="h-px flex-1 transition-colors"
                style={{
                  background: i <= stage ? "rgb(194 50 40)" : "rgba(0,0,0,0.12)",
                }}
              />
            ))}
          </div>
          <div className="mt-[10px] editorial-meta text-ink/55">
            {String(stage + 1).padStart(2, "0")} / {String(STAGES.length).padStart(2, "0")}
          </div>
        </div>

        <div className="mt-[40px] pr-[18px]">
          <CuratorBubble signature={curator.firstName}>
            «Обычно я подбираю за вечер — но первый человек в жизни юзера всегда приходит быстрее.
            Посидите минуту, я не тяну.»
          </CuratorBubble>
        </div>
      </div>

      <div className="px-[22px] pb-[24px] pt-[16px]">
        <motion.button
          type="button"
          onClick={() => {
            navigate("/home");
          }}
          disabled={!ready}
          initial={false}
          animate={{ opacity: ready ? 1 : 0.35 }}
          transition={{ duration: 0.5 }}
          className="w-full flex items-baseline justify-between border-t border-ink/22 pt-[14px] font-display italic font-light text-[22px] tracking-[-0.01em] text-ink disabled:cursor-wait"
        >
          <span>{ready ? "прочитать первое письмо" : "готовлю…"}</span>
          <span className="text-accent">→</span>
        </motion.button>
      </div>
    </Screen>
  );
}
